import React from 'react';
/* eslint-disable-next-line no-restricted-imports */
import styled from 'styled-components';

import getFontDetailsByLabel from '@headout/aer/src/tokens/typography';

import Conditional from 'Components/common/conditional';
import LSpan from 'Components/common/localizedTags/localizedSpan';

import { EmailBoxSvg } from 'Assets/svg/alert/EmailBoxSvg';
import { RightTickGreenSvg } from 'Assets/svg/alert/RightTickGreenSvg';

import { checkEmail } from 'Utils/validationUtils';

import { updateUserAttributes } from 'Actions/user';

import { strings } from 'Constants/strings';

import colors from 'Static/typography/colors';
import TYPE_LABELS from 'Static/typography/labels';

const Wrapper = styled.div`
	border-radius: 1rem;
	border: 1px solid ${colors.GREY_DS.G6};
	padding: 1.5rem 1.25rem 1.25rem;
	background: ${colors.WHITE};
`;

const AlertContainer = styled.div`
	opacity: 1;
	transition: all 0.2s ease;

	${props =>
		(props as any).hide &&
		`opacity: 0;
		height: 0;
		display: none;`}
`;

const AlertTextContainer = styled.div`
	display: flex;
`;

const LeftContainer = styled.div`
	.email-svg {
		width: 3.25rem;
		height: 3.25rem;
	}
`;

const RightContainer = styled.div`
	margin-left: 0.625rem;
`;

const InfoHeader = styled.div`
	span {
		${getFontDetailsByLabel(TYPE_LABELS.UI_LABEL_MEDIUM_HEAVY)};
		color: ${colors.GREY_DS.G3};
	}
`;

const InfoContent = styled.div`
	margin-top: 0.5rem;

	span {
		font-size: 0.875rem !important;
		line-height: 1.25rem !important;
		color: ${colors.GREY_DS.G3};
	}
`;

const EmailContainer = styled.div`
	text-align: center;
	margin-top: 1.25rem;
	color: ${colors.WHITE};

	input {
		height: 2.75rem;
		color: ${colors.CHARCOAL};
		width: 100%;
		padding: 0.75rem;
		border: solid 1px ${colors.GREY_DS.G6};
		border-radius: 0.25rem;
		${getFontDetailsByLabel(TYPE_LABELS.UI_LABEL_MEDIUM)};

		&:focus {
			outline: none;
		}
	}

	${props => (props as any).shake && `animation: shake 0.3s 1;`};

	@keyframes shake {
		12.5% {
			transform: translate(1.25rem, 0);
		}
		37.5% {
			transform: translate(-1.25rem, 0);
		}
		62.5% {
			transform: translate(1.25rem, 0);
		}
		87.5% {
			transform: translate(-1.25rem, 0);
		}
		100% {
			transform: translate(0, 0);
		}
	}
`;

const AlertButton = styled.div`
	border-radius: 0.25rem;
	text-align: center;
	margin-top: 0.75rem;
	color: ${colors.WHITE};
	pointer-events: all;
	cursor: pointer;
	padding: 0.6875rem 0 0.8125rem;
	background: ${colors.PURPS};

	.alert-button-text {
		${getFontDetailsByLabel(TYPE_LABELS.BUTTON_MEDIUM)};
	}
`;

const SuccessContainer = styled.div`
	opacity: 1;

	&.hide {
		opacity: 0;
		height: 0;
		display: none;
	}

	display: flex;
	justify-content: center;
	align-items: center;
	flex-direction: column;

	.success-svg {
		> svg {
			height: 6.25rem;
			width: 6.25rem;
			margin: 0.25rem auto 1.75rem;
		}
	}

	.success-heading {
		${getFontDetailsByLabel(TYPE_LABELS.HEADING_SMALL)};
		color: ${colors.GREY_DS.G3};
	}

	.success-text {
		${getFontDetailsByLabel(TYPE_LABELS.PARAGRAPH_REGULAR)};
		margin: 0.25rem auto;
		color: ${colors.GREY_DS.G3};
	}
`;

type Props = {
	tourId?: number;
	imageSvgSymbol?: string;
	alertHeader?: string;
	alertContent?: string;
	buttonText?: string;
	buttonColor?: string;
	sendAlertData?: (...args: any[]) => any;
	alertType?: string;
	currentCityCode?: string;
	inputPlaceholder?: any;
	user?: any;
};

const SideBarAlert = ({
	tourId,
	inputPlaceholder,
	alertHeader,
	alertContent,
	buttonText,
	sendAlertData,
	alertType,
	currentCityCode,
	user,
}: Props) => {
	const [email, setEmail] = React.useState('');
	const [hasShake, setHasShake] = React.useState(false);
	const [transitioning, setTransistioning] = React.useState(false);

	const onEmailInputChange = (e: any) => {
		setEmail(e.target.value);
		setHasShake(false);
	};

	const sendAnalyticsEvent = ({ alertEmail }: any) => {
		// Note: Alert email is different from user identify email
		updateUserAttributes({ alertEmail });
	};

	const sendData = (email: any) => {
		// @ts-expect-error TS(2722): Cannot invoke an object which is possibly 'undefin... Remove this comment to see the full error message
		sendAlertData({
			email,
			tourId,
			cityName: currentCityCode,
			alertType,
		});
		sendAnalyticsEvent({ alertEmail: email });
	};

	const handleClick = () => {
		if (user) {
			const { email: userEmail } = user;
			sendData(userEmail);
			setTransistioning(true);
		} else if (checkEmail(email)) {
			setEmail(email);
			setHasShake(false);
			setTransistioning(true);
			sendData(email);
		} else {
			setHasShake(true);
			setTimeout(() => {
				setHasShake(false);
			}, 1000);
		}
	};

	return (
		<Wrapper>
			{/* @ts-expect-error TS(2769): No overload matches this call. */}
			<AlertContainer hide={transitioning}>
				<AlertTextContainer>
					<LeftContainer>
						<EmailBoxSvg className='email-svg' />
					</LeftContainer>
					<RightContainer>
						<InfoHeader>
							<span className='block'>{alertHeader}</span>
						</InfoHeader>
						<InfoContent>
							<span className='block'>{alertContent}</span>
						</InfoContent>
					</RightContainer>
				</AlertTextContainer>

				<Conditional if={!user}>
					{/* @ts-expect-error TS(2769): No overload matches this call. */}
					<EmailContainer shake={hasShake}>
						<input
							type='text'
							placeholder={inputPlaceholder}
							onChange={onEmailInputChange}
						/>
					</EmailContainer>
				</Conditional>
				<AlertButton onClick={handleClick}>
					<span className='alert-button-text'>{buttonText}</span>
				</AlertButton>
			</AlertContainer>
			<SuccessContainer className={`${transitioning ? 'show' : 'hide'} `}>
				<div className='success-svg'>
					<RightTickGreenSvg />
				</div>
				<LSpan className='success-heading'>{strings.CMN_AWESOME}</LSpan>
				<LSpan className='success-text'>{strings.CMN_NOTIFY_YOU}</LSpan>
			</SuccessContainer>
		</Wrapper>
	);
};

export default SideBarAlert;
